/**
 * File Review View
 *
 * Displays file details and line items for review, similar to DIMP's Financial Files review page.
 */

import type * as React from "react";
import { useEffect, useCallback, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import type { SortingState } from "@tanstack/react-table";
import { ArrowLeft } from "lucide-react";
import { Loader, Button } from "@/ui/atoms";
import { EmptyState } from "@/ui/molecules";
import { cn } from "@/utils/utils";
import type { TSupportingFile } from "@/store";
import type { TFileReviewUseCases } from "./useCases";
import type { TReviewTabProps } from "./createReviewTab";
import type { TEtlTabProps } from "./createEtlTab";
import type { TGLFileDetails } from "@/ui/views/ViewGLLines";
import type { TSupportingFilesTabProps } from "./SupportingFilesTab";
import type { TSupportingFileUploadModalProps } from "./SupportingFileUploadModal";
import type { TSupportingFileEditModalProps } from "./SupportingFileEditModal";
import { FileReviewHeader } from "./FileReviewHeader";
import { FileReviewEmptyMonth } from "./FileReviewEmptyMonth";
import {
    formatMonthLabel,
    resolveSelectedFileType,
    resolveSlotFileId,
    toTypeSlug,
    toYearMonth,
} from "./submissionContext";
import {
    FileReviewTabs,
    FileReviewTabPlaceholder,
    parseTabId,
    type TFileReviewTabId,
} from "./FileReviewTabs";
import { OperatingStatementTab } from "./OperatingStatementTab";
import { OriginalFilesTab } from "./OriginalFilesTab";
import { SubmissionHistoryTab } from "./SubmissionHistoryTab";

export type { TFileReviewDetails, TFileSftpSubmission, TReviewLineItem } from "./types";

export type TViewFileReviewParams = {
    useCases: () => TFileReviewUseCases;
    ReviewTab: React.ComponentType<TReviewTabProps>;
    /** Optional so existing call sites without an ETL tab keep falling to the placeholder. */
    EtlTab?: React.ComponentType<TEtlTabProps>;
    /** Optional for the same reason as `EtlTab`; bound to its own data by the composition root. */
    GeneratedGLTab?: React.ComponentType<{ file: TGLFileDetails }>;
    SupportingFilesTab: React.ComponentType<TSupportingFilesTabProps>;
    SupportingFileUploadModal: React.ComponentType<TSupportingFileUploadModalProps>;
    SupportingFileEditModal: React.ComponentType<TSupportingFileEditModalProps>;
};

export type TViewFileReviewProps = Record<string, never>;

export const createViewFileReview = ({
    useCases,
    ReviewTab,
    EtlTab,
    GeneratedGLTab,
    SupportingFilesTab,
    SupportingFileUploadModal,
    SupportingFileEditModal,
}: TViewFileReviewParams) => {
    return function ViewFileReview() {
        const { fileId } = useParams<{ fileId: string }>();
        const reviewUseCases = useCases();
        const {
            onMount,
            onMountRestricted,
            onUnmount,
            useFileState,
            onBack,
            onAccessDenied,
            useFinjournalReviewCaps,
            useTbglReviewCaps,
            useSubmissionContext,
            useSupportingFileCaps,
            useSupportingFilesState,
            fetchSupportingFiles,
            useRelatableFiles,
            onUploadSupportingFile,
            onUpdateSupportingFile,
            onDeleteSupportingFile,
            onDownloadSupportingFile,
            loadSubmissionContext,
            onOpenSiblingFile,
            loadOriginalPreview,
            onDownloadOriginal,
            useOperatingStatementEnabled,
            loadOperatingStatement,
            onDownloadOperatingStatement,
            useSubmissionHistory,
            loadSubmissionHistory,
            onDownloadErrorReport,
        } = reviewUseCases;

        const fileState = useFileState();
        const finjournalCaps = useFinjournalReviewCaps();
        const tbglCaps = useTbglReviewCaps();
        const operatingStatementEnabled = useOperatingStatementEnabled();
        // useSearchParams and useSubmissionContext must be called here, above the
        // early returns below, to satisfy the rules of hooks — only their values
        // are used further down in the render body.
        const [searchParams, setSearchParams] = useSearchParams();
        const submissionContext = useSubmissionContext();
        const submissionHistory = useSubmissionHistory();

        // Supporting-files modal state and scope also have to sit above the early
        // returns (rules of hooks). `file` is only defined below them, so the deal
        // and period fall back to the raw file state instead.
        const [uploadOpen, setUploadOpen] = useState(false);
        const [editing, setEditing] = useState<TSupportingFile | null>(null);
        const [refreshKey, setRefreshKey] = useState(0);
        const handleSupportingChanged = useCallback(() => setRefreshKey((n) => n + 1), []);
        const supportingCaps = useSupportingFileCaps();
        const supportingFilesState = useSupportingFilesState();
        const contextData = submissionContext.status === "rejected" ? null : submissionContext.data;

        // FileReviewTabs' per-type option fires onSelectFileType then onSelectTab
        // synchronously from a single radio's onChange, and every handler below
        // that edits the URL builds its patch from the same `searchParams`
        // snapshot captured by this render. react-router's setSearchParams
        // replaces the query string wholesale rather than merging across calls,
        // so two such calls in one tick would otherwise have the second silently
        // discard the first's edit — confirmed directly: without this, picking a
        // type landed as an empty query string. This ref carries the running
        // edit across same-tick calls; the effect clears it once the URL actually
        // reflects it (or once any other navigation lands), so a later, separate
        // interaction always starts from the real URL rather than a stale chain.
        const pendingSearchParamsRef = useRef<URLSearchParams | null>(null);
        useEffect(() => {
            pendingSearchParamsRef.current = null;
        }, [searchParams]);
        // Invariant: every handler below that edits the URL must go through this —
        // never call setSearchParams directly. A direct call would build its patch
        // from the same stale `searchParams` snapshot the ref above exists to work
        // around, silently reintroducing the same-tick clobber this was written to fix.
        const patchSearchParams = useCallback(
            (mutate: (next: URLSearchParams) => void) => {
                const next = new URLSearchParams(pendingSearchParamsRef.current ?? searchParams);
                mutate(next);
                pendingSearchParamsRef.current = next;
                setSearchParams(next, { replace: true });
            },
            [searchParams, setSearchParams],
        );

        // Review actions are gated per the permissions matrix by file type. FinJournal
        // files use the FinJournal caps; TB/GL/OTB/TBGL files use the TB/GL caps.
        const isFinJournal = fileState.data?.fileType === "financial_journals";

        // Whether the caller might only hold the Supporting Files grant. Caps-only, so
        // it is known before any request: every staff role holds `tbgl_review`, so this
        // is true for Business Partners (and BP+) alone and staff take the path they
        // always took. It is deliberately NOT the final answer — see below.
        const mightBeRestricted = supportingCaps.canView && !tbglCaps.canReview;

        // The server settles it. A BP+ holds `finjournal_review`, so on a FinJournal the
        // context comes back unrestricted and they get the whole page; on a TB/GL file it
        // comes back restricted and they get the tab. The shell cannot tell those apart
        // on its own, because knowing the file's type means loading a file it may not be
        // allowed to load.
        //
        // Only the context describing THIS file may answer it. The store deliberately
        // keeps the previous file's context visible while the next request is in flight
        // (so the header does not blank during a switch), and the answer is per file:
        // a BP+ leaving a FinJournal they may review for a TB/GL file they may not would
        // otherwise carry the "unrestricted" verdict across, fire the review mount, and
        // sit on a 403 error screen until the new context landed. Falling back to null —
        // i.e. treating an unmatched context as "restricted until told otherwise" — keeps
        // the no-403-requests invariant through the gap.
        //
        // Note this compares the anchor file, not the period: a month switch asks for the
        // *same* file id with `?period=`, so it stays matched and does not flicker.
        const contextForThisFile = contextData?.fileId === fileId ? contextData : null;
        const contextRestricted = contextForThisFile?.restricted ?? null;
        const isRestricted = mightBeRestricted && contextRestricted !== false;

        // Nothing on the page is granted at all — redirect rather than show an empty
        // screen. Only reachable on the unrestricted path, where the file does load.
        const canReviewThisFile = isFinJournal ? finjournalCaps.canReview : tbglCaps.canReview;
        // The Supporting Files escape hatch only exists for the `mightBeRestricted`
        // population (BP/BP+): they never reach this line with `fileState.data` set
        // (they stay context-only until the server promotes them), so gating it on
        // `mightBeRestricted` costs that population nothing. Without the gate, a
        // caller who holds `tbgl_review` but not `finjournal_review` for THIS file
        // would silently escape the redirect via `supporting_files_view` alone — a
        // grant every role holds — turning a real safety net into a dead one.
        const reviewBlocked =
            !!fileState.data &&
            !canReviewThisFile &&
            !(mightBeRestricted && supportingCaps.canView);
        useEffect(() => {
            if (reviewBlocked) onAccessDenied();
        }, [reviewBlocked, onAccessDenied]);

        // Two effects rather than one, because the two mounts fire on different
        // triggers: the first on the file, the second on the server's answer about it.
        // Folding them together re-runs the context load every time that answer changes.
        useEffect(() => {
            if (!fileId) return;
            // Context-only for a possibly-restricted caller: it is the one request every
            // role may make, and it is what tells us which of the two they are.
            if (mightBeRestricted) {
                onMountRestricted(fileId);
                return;
            }
            onMount(fileId);
            return onUnmount;
        }, [fileId, mightBeRestricted, onMount, onMountRestricted, onUnmount]);

        // Unrestricted after all (BP+ on a FinJournal): the server admitted them, so load
        // the rest of the page. Runs at most once per file — `contextRestricted` only
        // reaches `false` from `null`, and never goes back.
        useEffect(() => {
            if (!fileId || !mightBeRestricted || contextRestricted !== false) return;
            onMount(fileId);
            return onUnmount;
        }, [fileId, mightBeRestricted, contextRestricted, onMount, onUnmount]);

        const handleBackToHome = useCallback(() => {
            onBack();
        }, [onBack]);

        const activeTab = isRestricted ? "supporting" : parseTabId(searchParams.get("tab"));
        const handleSelectTab = useCallback(
            (tab: TFileReviewTabId) => {
                patchSearchParams((next) => {
                    // "review" is the default, so it stays out of the URL rather than
                    // pinning ?tab=review onto every link the user copies.
                    if (tab === "review") next.delete("tab");
                    else next.set("tab", tab);
                });
            },
            [patchSearchParams],
        );

        // The month being viewed lives in the URL, so it survives a reload and a
        // shared link. Absent means "the file's own month", which stays out of the
        // URL — `/files/:id` is a file's canonical address. A restricted caller has
        // no file to name a month with, so its own context's period stands in —
        // `fileState` stays "idle" forever for them, so `filePeriod` alone would
        // leave them with no month at all until the context resolves.
        const periodParam = searchParams.get("period");
        const filePeriod = fileState.data !== null ? toYearMonth(fileState.data.period) : null;
        const contextPeriod = contextData !== null ? toYearMonth(contextData.period) : null;
        const selectedPeriod = periodParam ?? filePeriod ?? contextPeriod;
        // Everything below is gated on this rather than on the context alone. A file
        // opened directly may not be its period's *active* file (a superseded attempt
        // still has a URL), and switching the user off the file they asked for is not
        // month switching — it is losing their place.
        //
        // There is deliberately no effect that strips `?period=` back off once it
        // agrees with the file's own month after a sibling switch — a prior attempt
        // at that caused a second, redundant submission-context request for the same
        // month once it fired. The URL keeping a `?period=` equal to the loaded
        // file's own month is a harmless, tolerated leftover: `isOtherMonthSelected`
        // is false in that case, so the page renders exactly as if the param were
        // absent. The same is true of `?type=` below, for the same reason.
        const isOtherMonthSelected = periodParam !== null && periodParam !== filePeriod;

        // The one and only place the submission context is fetched, for every caller
        // — restricted or not. A restricted caller has no `onMount` to piggyback on
        // (it would issue the review-only requests this mode exists to avoid), so
        // this effect — keyed on `fileId`/`periodParam` alone — is what actually
        // loads their context; `onMountRestricted` only established
        // `currentFileIdRef` for this request's response to land against.
        useEffect(() => {
            if (!fileId) return;
            void loadSubmissionContext(fileId, periodParam ?? undefined);
        }, [fileId, periodParam, loadSubmissionContext]);

        // Only slots that actually describe the selected month can be acted on: the
        // context keeps the previous month's data visible while the next request is in
        // flight, and reading those slots would resolve the wrong file.
        const contextForSelectedPeriod =
            contextData !== null &&
            selectedPeriod !== null &&
            toYearMonth(contextData.period) === selectedPeriod
                ? contextData
                : null;
        const typeParam = searchParams.get("type");
        const selectedFileType = resolveSelectedFileType(
            typeParam,
            contextForSelectedPeriod,
            fileState.data?.fileType ?? "",
        );
        // A divergent-selection gate covering both axes the URL can carry — a
        // picked month, a picked type, or both. `isOtherTypeSelected` mirrors
        // `isOtherMonthSelected` above, including its reasoning: there is no
        // effect stripping `?type=` back off once it agrees with the loaded
        // file's own type, because the URL keeping it is a harmless, tolerated
        // leftover once `isOtherTypeSelected` is false again.
        const isOtherTypeSelected =
            fileState.data !== null && selectedFileType !== fileState.data.fileType;
        const isOtherSelection = isOtherMonthSelected || isOtherTypeSelected;
        const selectedMonthFileId = resolveSlotFileId(contextForSelectedPeriod, selectedFileType);

        useEffect(() => {
            if (!isOtherSelection) return;
            if (selectedMonthFileId === null || selectedMonthFileId === fileId) return;
            // Same route, so this refetches in place. The URL keeps carrying the picked
            // period and type through the switch and afterwards (see the notes by
            // isOtherMonthSelected and isOtherTypeSelected above), the context reloads
            // for the new file, and this slot resolves to the file itself — which is
            // what settles this effect instead of looping, on either axis.
            onOpenSiblingFile(selectedMonthFileId, {
                ...(activeTab === "review" ? {} : { tab: activeTab }),
                period: periodParam ?? undefined,
                type: typeParam ?? undefined,
            });
        }, [
            isOtherSelection,
            selectedMonthFileId,
            fileId,
            onOpenSiblingFile,
            activeTab,
            periodParam,
            typeParam,
        ]);

        const handleSelectPeriod = useCallback(
            (period: string) => {
                patchSearchParams((next) => {
                    if (period === filePeriod) next.delete("period");
                    else next.set("period", period);
                });
            },
            [patchSearchParams, filePeriod],
        );

        const handleSelectFileType = useCallback(
            (fileType: string) => {
                patchSearchParams((next) => {
                    const slug = toTypeSlug(fileType);
                    if (slug === null || fileType === fileState.data?.fileType) next.delete("type");
                    else next.set("type", slug);
                });
            },
            [patchSearchParams, fileState.data],
        );

        // The context-error panel's "Try again" action. Re-issues the same request
        // the mount/param-change effect above just made for `periodParam` — there is
        // no separate retry endpoint, only the ordinary load repeated.
        const handleRetryContext = useCallback(() => {
            if (!fileId) return;
            void loadSubmissionContext(fileId, periodParam ?? undefined);
        }, [fileId, periodParam, loadSubmissionContext]);

        // Sorting is a choice about a column, not about a file, so it is the one piece
        // of table state that follows the user across a month switch. Everything else —
        // page, column filters, open modals, expanded splits, banners, download type —
        // is dropped by remounting the tab below, because it all describes the file
        // being left behind.
        const sortRef = useRef<SortingState | null>(null);
        const handleSortingChanged = useCallback((sorting: SortingState) => {
            sortRef.current = sorting;
        }, []);

        // The Supporting Files tab tracks the month picker, not just the anchor
        // file's own month: `contextForSelectedPeriod` is null while a month switch
        // is in flight (see above), so the raw file state is the fallback until the
        // new month's context lands. A restricted caller has no file at all, so once
        // its context resolves this is the only source either field has.
        //
        // That fallback is withheld while a *different* month is selected. The anchor
        // file still describes the month the user just left, so falling back to it
        // would scope the tab — and the upload modal it hands `period` to — to the
        // previous month for the length of the context request, landing uploads on
        // the wrong submission. Null instead: the tab already renders a loader for
        // it (and the context error's retry), the same states it shows a restricted
        // caller before their context arrives.
        const supportingAnchor = isOtherMonthSelected ? null : fileState.data;
        const supportingPeriod =
            contextForSelectedPeriod?.period ?? supportingAnchor?.period ?? null;
        const supportingDealId =
            contextForSelectedPeriod?.dealId ?? supportingAnchor?.dealId ?? null;
        const supportingDealName =
            contextForSelectedPeriod?.submissionName ?? supportingAnchor?.dealName ?? null;
        const relatableFiles = useRelatableFiles(supportingDealId);

        // The history is fetched the first time its view is shown rather than on
        // mount: most visits to this page never open it. It re-requests when the
        // file or the selected month changes, which is what makes month switching
        // work here too.
        //
        // The period comes from `selectedPeriod` — the URL, falling back to the
        // open file's own month — never from the submission context. The context
        // keeps the previous month's data while a request is in flight, so reading
        // its period would ask for the wrong month.
        //
        // `fileLoadedForRoute` is the same guard the content panel uses: `fileState`
        // keeps the file being left behind for the length of a switch, so
        // `filePeriod` can still describe *that* file. Requesting on it would ask
        // for a month that may not even exist in the new deal's range, which the
        // endpoint rejects outright with PERIOD_OUT_OF_RANGE. Waiting for the
        // route's own file to land costs nothing: the tab shows its spinner until
        // the first response.
        //
        // Re-entering the view deliberately re-requests. An earlier version kept a
        // "already asked for this file+month" ref to suppress that, which made a
        // failed load permanent — the ref still matched, so returning to the view
        // skipped the fetch and left the error on screen with nothing able to clear
        // it. The attempts list is also exactly the kind of thing that changes
        // underneath you (someone re-uploads), so a fetch per open is the more
        // honest behaviour, and the endpoint is a small metadata read.
        const fileLoadedForRoute = fileState.data?.id === fileId;
        const historyPeriod = fileLoadedForRoute ? selectedPeriod : null;
        useEffect(() => {
            if (activeTab !== "history" || !fileId || historyPeriod === null) return;
            void loadSubmissionHistory(fileId, historyPeriod);
        }, [activeTab, fileId, historyPeriod, loadSubmissionHistory]);

        // The history panel's "Try again": the same load the effect above just made.
        const handleRetryHistory = useCallback(() => {
            if (!fileId || historyPeriod === null) return;
            void loadSubmissionHistory(fileId, historyPeriod);
        }, [fileId, historyPeriod, loadSubmissionHistory]);

        // Unlike a month, a different file type is a different column set — a TB
        // sort key can name a column GL does not have — so the carried sort must
        // not survive a type change the way it survives a month change. The ref
        // holds the previous type so this only clears on an actual change, never
        // on first render (where "previous" and "current" are the same value).
        const previousSelectedFileTypeRef = useRef(selectedFileType);
        useEffect(() => {
            if (previousSelectedFileTypeRef.current !== selectedFileType) {
                sortRef.current = null;
                previousSelectedFileTypeRef.current = selectedFileType;
            }
        }, [selectedFileType]);

        // Loading state.
        //
        // "idle" counts as loading here, not just "pending". A BP+ promoted off
        // restricted mode has already flipped `isRestricted` to false, but the effect
        // that fires `onMount` has not run yet — so the file is not missing, it has not
        // been asked for. Treating only "pending" as in-flight let that frame fall all
        // the way through to the "File not found" branch below.
        if (
            !isRestricted &&
            !fileState.data &&
            (fileState.status === "pending" || fileState.status === "idle")
        ) {
            return (
                <div className="flex items-center justify-center py-12">
                    <Loader size="lg" />
                </div>
            );
        }

        // Error state
        if (!isRestricted && fileState.status === "rejected") {
            const stateError = (fileState as { error: Error | null }).error;
            const errorMessage =
                stateError instanceof Error ? stateError.message : "Failed to load file";

            return (
                <div className="space-y-6">
                    <div className="text-center py-8">
                        <p className="text-destructive">{errorMessage}</p>
                        <Button variant="outline" onClick={handleBackToHome} className="mt-4">
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Home
                        </Button>
                    </div>
                </div>
            );
        }

        const file = fileState.data ?? null;
        if (!isRestricted && !file) {
            return (
                <EmptyState
                    variant="error"
                    title="File not found"
                    description="The requested file could not be found."
                    action={{ label: "Back to Home", onClick: handleBackToHome }}
                />
            );
        }

        // What the Review panel shows, in priority order. Anything in flight beats the
        // empty state: concluding "this month has nothing" from a half-arrived context
        // flashes a wrong answer at the user. A failed context for the selected month is
        // checked first, ahead of "loading": a rejected context also satisfies
        // isAwaitingSelectedMonth below, and without this it would spin forever with no
        // event left to resolve it. Scoped to isOtherSelection — a rejected context on
        // the file's own month and type keeps behaving exactly as before this ticket,
        // where the review page stays fully usable.
        const isContextErrorForSelectedMonth =
            isOtherSelection && submissionContext.status === "rejected";
        const isAwaitingSelectedMonth = isOtherSelection && contextForSelectedPeriod === null;
        // The `selectedMonthFileId !== fileId` clause mirrors the navigate effect's own
        // guard above, so the two never disagree about whether a sibling switch is
        // still pending.
        // `file` is null in restricted mode, where the Review panel this feeds never
        // renders — the second disjunct is simply inert there rather than a real check.
        const isAwaitingSiblingFile =
            (isOtherSelection && selectedMonthFileId !== null && selectedMonthFileId !== fileId) ||
            // Null-guarded: a restricted caller never loads a file, and this expression
            // is evaluated above the early returns that would otherwise prove it non-null.
            (file !== null && file.id !== fileId);
        const reviewPanelMode: "review" | "loading" | "empty-month" | "context-error" =
            isContextErrorForSelectedMonth
                ? "context-error"
                : isAwaitingSelectedMonth || isAwaitingSiblingFile
                  ? "loading"
                  : isOtherSelection && selectedMonthFileId === null
                    ? "empty-month"
                    : "review";

        // What a file-bound view shows in place of its content while the selection has
        // no file of its own to show: loading, a failed month, or an empty month. Shared
        // by Review and Generated GL, which would otherwise show the anchor file's data
        // under another month's header.
        const selectionFallback =
            reviewPanelMode === "context-error" ? (
                <div data-testid="file-review-content-error">
                    <EmptyState
                        variant="error"
                        title={`We couldn't load ${formatMonthLabel(selectedPeriod ?? "")}`}
                        description="Something went wrong loading this month."
                        action={{ label: "Try again", onClick: handleRetryContext }}
                    />
                </div>
            ) : reviewPanelMode === "loading" ? (
                // Only the panel spins. The header, the month picker and the
                // navigation stay put, which is what makes a month change read
                // as a change of contents rather than a page reload.
                <div
                    data-testid="file-review-content-loading"
                    className="flex items-center justify-center py-12"
                >
                    <Loader size="lg" />
                </div>
            ) : (
                <FileReviewEmptyMonth fileType={selectedFileType} period={selectedPeriod ?? ""} />
            );

        return (
            <div className="space-y-6">
                {/* Breadcrumb + Back to Home */}
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-muted-foreground mt-1">
                            <button
                                onClick={handleBackToHome}
                                className="hover:text-foreground hover:underline"
                            >
                                Home
                            </button>
                            {" > "}
                            <span>{file?.originalFilename ?? "Supporting files"}</span>
                        </p>
                    </div>
                    <Button variant="outline" onClick={handleBackToHome}>
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Home
                    </Button>
                </div>

                <FileReviewHeader
                    file={file}
                    context={submissionContext}
                    selectedPeriod={selectedPeriod ?? ""}
                    onSelectPeriod={handleSelectPeriod}
                />

                <div className="space-y-3">
                    <h2 className="text-lg font-semibold">Complete this submission</h2>
                    <FileReviewTabs
                        file={file}
                        context={submissionContext}
                        activeTab={activeTab}
                        onSelectTab={handleSelectTab}
                        restricted={isRestricted}
                        selectedFileType={selectedFileType}
                        onSelectFileType={handleSelectFileType}
                        tbglCaps={tbglCaps}
                        finjournalCaps={finjournalCaps}
                        operatingStatementEnabled={operatingStatementEnabled}
                    />
                </div>

                {/* The content sits in a panel tinted like the selected navigation group,
                    so what you picked above and what you are looking at below read as one
                    thing rather than two stacked bands. */}
                <div className="rounded-xl border border-primary/40 p-4">
                    {/* The Review panel stays mounted: useServerTable holds page, sorting and
                        column filters in local state with no URL sync, so unmounting on a tab
                        switch would silently discard the user's table state. A restricted
                        caller has no file to give it, and no Review tab to switch to, so it
                        is not rendered at all rather than hidden. */}
                    {!isRestricted && file !== null && (
                        <div className={cn(activeTab !== "review" && "hidden")}>
                            {reviewPanelMode === "review" ? (
                                // Keyed on the file: a month switch gets a clean table, while a
                                // tab switch (which does not change the key) keeps the one the
                                // user has set up — only the wrapper's `hidden` class changes.
                                <ReviewTab
                                    key={file.id}
                                    file={file}
                                    useCases={reviewUseCases}
                                    initialSort={sortRef.current ?? undefined}
                                    onSortingChanged={handleSortingChanged}
                                />
                            ) : (
                                selectionFallback
                            )}
                        </div>
                    )}
                    {activeTab === "supporting" ? (
                        /* This tab is built, so it must never fall through to the
                           "coming soon" placeholder. A restricted caller has no file,
                           so its period arrives only with the submission context: until
                           then this is loading, and if that request fails it is an error
                           with a retry — not a feature that does not exist yet. */
                        supportingPeriod === null ? (
                            submissionContext.status === "rejected" ? (
                                <EmptyState
                                    variant="error"
                                    title="Couldn't load this submission"
                                    description="Supporting files for this month could not be loaded."
                                    action={{ label: "Try again", onClick: handleRetryContext }}
                                />
                            ) : (
                                <div className="flex items-center justify-center py-12">
                                    <Loader size="lg" />
                                </div>
                            )
                        ) : (
                            <SupportingFilesTab
                                dealId={supportingDealId}
                                period={supportingPeriod}
                                filesState={supportingFilesState}
                                fetchData={fetchSupportingFiles}
                                onDownload={onDownloadSupportingFile}
                                onDelete={onDeleteSupportingFile}
                                canUpload={supportingCaps.canUpload}
                                canEdit={supportingCaps.canEdit}
                                canDelete={supportingCaps.canDelete}
                                canDownload={supportingCaps.canDownload}
                                onRequestUpload={() => setUploadOpen(true)}
                                onRequestEdit={setEditing}
                                refreshKey={refreshKey}
                            />
                        )
                    ) : activeTab === "original" ? (
                        <OriginalFilesTab
                            context={submissionContext}
                            /* A submission's slots can mix file types (a deal-scoped
                               FinJournal can sit beside a TB on the same deal), so each
                               row is gated by its own file type's capability rather than
                               the open file's — the tab itself picks per row. */
                            tbglCaps={tbglCaps}
                            finjournalCaps={finjournalCaps}
                            loadPreview={loadOriginalPreview}
                            onDownload={onDownloadOriginal}
                        />
                    ) : activeTab === "etl" ? (
                        EtlTab && file !== null ? (
                            <EtlTab file={file} useCases={reviewUseCases} />
                        ) : (
                            <FileReviewTabPlaceholder tab={activeTab} />
                        )
                    ) : activeTab === "generated-gl" ? (
                        GeneratedGLTab && file !== null ? (
                            // Month-scoped, not type-scoped: these are the open TB's
                            // lines, so a selected review type with no file this month
                            // must not hide them. Anything else the Review panel would
                            // not show yet (another month, a sibling still loading)
                            // falls back the same way it does there.
                            !isOtherMonthSelected && file.id === fileId ? (
                                // Keyed on the file, like ReviewTab: its table state and
                                // fetched lines belong to one file, and a sibling switch
                                // keeps this view mounted while the next file loads.
                                <GeneratedGLTab key={file.id} file={file} />
                            ) : (
                                selectionFallback
                            )
                        ) : (
                            <FileReviewTabPlaceholder tab={activeTab} />
                        )
                    ) : activeTab === "operating-statement" && operatingStatementEnabled ? (
                        // Same month/type fallback as Generated GL: the statement is the
                        // open TB's, so it only shows once that file is the one loaded.
                        file !== null && !isOtherMonthSelected && file.id === fileId ? (
                            <OperatingStatementTab
                                key={file.id}
                                fileId={file.id}
                                loadStatement={loadOperatingStatement}
                                onDownload={onDownloadOperatingStatement}
                                canDownload={tbglCaps.canDownload}
                            />
                        ) : (
                            selectionFallback
                        )
                    ) : activeTab === "history" ? (
                        <SubmissionHistoryTab
                            history={submissionHistory}
                            /* Per-row, not per-anchor-file: the history endpoint applies
                               no file-type filter, so a TB-anchored history can contain
                               FinJournal rows (and vice versa), each gated on its own
                               cap — the same reasoning the Original Files tab uses. */
                            canDownload={(fileType) =>
                                fileType === "financial_journals"
                                    ? finjournalCaps.canDownload
                                    : tbglCaps.canDownload
                            }
                            onDownloadErrorReport={onDownloadErrorReport}
                            onRetry={handleRetryHistory}
                        />
                    ) : (
                        activeTab !== "review" && <FileReviewTabPlaceholder tab={activeTab} />
                    )}
                </div>

                {/* Mounted beside the tab (not inside it) so a list refetch triggered by
                    an upload/edit can never unmount an open form mid-submission. */}
                {supportingDealId !== null && supportingPeriod !== null && (
                    <>
                        <SupportingFileUploadModal
                            open={uploadOpen}
                            onOpenChange={setUploadOpen}
                            dealId={supportingDealId}
                            dealName={supportingDealName}
                            period={supportingPeriod}
                            relatableFiles={relatableFiles}
                            onUpload={(input) =>
                                onUploadSupportingFile({
                                    dealId: supportingDealId,
                                    period: supportingPeriod,
                                    ...input,
                                })
                            }
                            onUploaded={handleSupportingChanged}
                        />
                        {/* Remounts per edit target (row A -> row B without passing through
                            `null`) rather than relying on the modal's own useEffect, which
                            resets form state a render after the swap and would otherwise
                            show row A's values for one frame. */}
                        <SupportingFileEditModal
                            key={editing?.id ?? "closed"}
                            file={editing}
                            onOpenChange={(open) => !open && setEditing(null)}
                            relatableFiles={relatableFiles}
                            onSave={onUpdateSupportingFile}
                            onSaved={() => {
                                handleSupportingChanged();
                                setEditing(null);
                            }}
                        />
                    </>
                )}
            </div>
        );
    };
};
